'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import type { Listing } from './DashboardInteractive';

interface ListingsTableProps {
  listings: Listing[];
  selectedIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectRow: (id: string, checked: boolean) => void;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-700',
  sold: 'bg-blue-100 text-blue-700',
};

const CONDITION_STYLES: Record<string, string> = {
  new: 'bg-green-100 text-green-700',
  used: 'bg-orange-100 text-orange-700',
  refurbished: 'bg-purple-100 text-purple-700',
};

function formatPrice(price: number) {
  return '€' + price.toLocaleString('en-US');
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
}

export default function ListingsTable({ listings, selectedIds, onSelectAll, onSelectRow }: ListingsTableProps) {
  const allSelected = listings.length > 0 && selectedIds.length === listings.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < listings.length;

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--color-muted)] border-b border-[var(--color-border)]">
              <th className="w-10 px-3 py-2.5 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={e => onSelectAll(e.target.checked)}
                  className="rounded border-[var(--color-border)] accent-[var(--color-primary)]"
                  aria-label="Select all listings"
                />
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap">Title</th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap">Status</th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap hidden md:table-cell">Category</th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap">Price</th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap hidden lg:table-cell">Condition</th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap hidden lg:table-cell">Location</th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap hidden xl:table-cell">Owner Email</th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap hidden xl:table-cell">Updated</th>
              <th className="px-3 py-2.5 text-left font-semibold text-[var(--color-foreground)] whitespace-nowrap">Edit</th>
            </tr>
          </thead>
          <tbody>
            {listings.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center text-[var(--color-muted-foreground)]">
                  <Icon name="InboxIcon" size={32} variant="outline" className="mx-auto mb-2 opacity-40" />
                  <p>No listings match your filters.</p>
                </td>
              </tr>
            ) : (
              listings.map((listing, idx) => (
                <tr
                  key={listing.id}
                  className={`border-b border-[var(--color-border)] hover:bg-[var(--color-muted)] transition-colors ${idx % 2 === 0 ? '' : 'bg-[var(--color-background)]'}`}
                >
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(listing.id)}
                      onChange={e => onSelectRow(listing.id, e.target.checked)}
                      className="rounded border-[var(--color-border)] accent-[var(--color-primary)]"
                      aria-label={`Select ${listing.title}`}
                    />
                  </td>
                  <td className="px-3 py-2.5 max-w-[200px]">
                    <span className="font-medium text-[var(--color-foreground)] truncate block" title={listing.title}>{listing.title}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[listing.status]}`}>
                      {listing.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[var(--color-muted-foreground)] hidden md:table-cell whitespace-nowrap">{listing.category}</td>
                  <td className="px-3 py-2.5 font-mono text-[var(--color-foreground)] whitespace-nowrap">{formatPrice(listing.price)}</td>
                  <td className="px-3 py-2.5 hidden lg:table-cell">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${CONDITION_STYLES[listing.condition]}`}>
                      {listing.condition}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[var(--color-muted-foreground)] hidden lg:table-cell whitespace-nowrap">{listing.location}</td>
                  <td className="px-3 py-2.5 text-[var(--color-muted-foreground)] hidden xl:table-cell text-xs">{listing.owner_email}</td>
                  <td className="px-3 py-2.5 text-[var(--color-muted-foreground)] hidden xl:table-cell text-xs whitespace-nowrap">{formatDate(listing.updated_at)}</td>
                  <td className="px-3 py-2.5">
                    <Link
                      href={`/comprehensive-listing-editor-panel?id=${listing.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[var(--color-primary)] text-[var(--color-primary-foreground)] rounded hover:opacity-90 transition-opacity"
                      aria-label={`Edit ${listing.title}`}
                    >
                      <Icon name="PencilSquareIcon" size={13} variant="solid" />
                      Edit
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}